/*
 * @Author: youfanrong 737064439@qq.com
 * @Date: 2025-03-20 16:25:18
 * @LastEditors: youfanrong 737064439@qq.com
 * @LastEditTime: 2025-08-29 11:35:41
 * @FilePath: /bar-front-end/src/utils/electronBridge.ts
 */
// 声明 electronBridge 类型
declare global {
  interface Window {
    electronBridge?: {
      postMessage: (message: any) => void
      // 添加监听消息的方法
      addEventListener?: (event: string, callback: (event: any) => void) => void
      // 添加连接检查相关的方法
      onConnectionCheck?: (callback: (data: any) => void) => void
      offConnectionCheck?: (callback: (data: any) => void) => void
      notifyPageVisible?: () => void
    }
  }
}

interface ElectronMessage {
  action: 'print' | 'saveToken' | 'toPrintList' | 'check-websocket-connection'
  data?: any
  token?: string
}

// 消息回调函数类型
type MessageCallback = (message: any) => void

/**
 * 判断是否在 Electron 环境中
 */
export const isInElectron = (): boolean => {
  return !!window?.electronBridge?.postMessage
}

/**
 * 监听来自 Electron 的消息
 */
export const onElectronMessage = (callback: MessageCallback): void => {
  console.log('初始化 Electron 消息监听，electronBridge:', window.electronBridge)
  console.log(
    'electronBridge 方法:',
    window.electronBridge ? Object.keys(window.electronBridge) : 'electronBridge 不存在'
  )

  if (isInElectron()) {
    console.log('在 Electron 环境中，尝试多种监听方式')

    // ⭐ 方式0：使用专门的 onConnectionCheck 方法（最可能的方式）
    if (window.electronBridge?.onConnectionCheck) {
      console.log('使用 electronBridge.onConnectionCheck 监听连接检查消息')
      window.electronBridge.onConnectionCheck((data) => {
        console.log('通过 electronBridge.onConnectionCheck 收到消息：', data)
        // 连接检查消息，直接调用 callback 并传入连接检查标识
        callback('check-websocket-connection')
      })
    } else {
      console.log('electronBridge.onConnectionCheck 不存在')
    }

    // 方式1：使用 electronBridge.addEventListener
    if (window.electronBridge?.addEventListener) {
      console.log('使用 electronBridge.addEventListener 监听消息')
      window.electronBridge.addEventListener('message', (event) => {
        console.log('通过 electronBridge.addEventListener 收到消息：', event)
        callback(event.data || event)
      })

      // 也尝试监听其他可能的事件名
      window.electronBridge.addEventListener('check-websocket-connection', (event) => {
        console.log('通过 addEventListener 收到连接检查消息：', event)
        callback('check-websocket-connection')
      })

      window.electronBridge.addEventListener('connectionCheck', (event) => {
        console.log('通过 addEventListener 收到 connectionCheck 消息：', event)
        callback('check-websocket-connection')
      })
    } else {
      console.log('electronBridge.addEventListener 不存在')
    }

    // 方式2：使用 window.addEventListener
    console.log('同时使用 window.addEventListener 监听消息')
    window.addEventListener('message', (event) => {
      console.log('通过 window.addEventListener 收到消息：', event)
      console.log('消息来源：', event.origin, '数据：', event.data)
      callback(event.data)
    })

    // 方式3：尝试直接监听 electronBridge 上的其他可能的事件方法
    if (window.electronBridge) {
      // 检查 electronBridge 是否有其他监听方法
      const bridge = window.electronBridge as any
      if (typeof bridge.on === 'function') {
        console.log('使用 electronBridge.on 监听消息')
        bridge.on('message', (data: any) => {
          console.log('通过 electronBridge.on 收到消息：', data)
          callback(data)
        })
      }

      if (typeof bridge.addListener === 'function') {
        console.log('使用 electronBridge.addListener 监听消息')
        bridge.addListener('message', (data: any) => {
          console.log('通过 electronBridge.addListener 收到消息：', data)
          callback(data)
        })
      }
    }
  } else {
    console.log('不在 Electron 环境中，无法监听消息')
  }
}

/**
 * 测试消息监听（仅用于调试）
 */
export const testMessageListening = (): void => {
  console.log('=== 开始测试消息监听 ===')
  console.log('window.electronBridge:', window.electronBridge)
  console.log('isInElectron():', isInElectron())

  if (window.electronBridge) {
    const bridge = window.electronBridge as any
    console.log('electronBridge 所有属性和方法：', Object.getOwnPropertyNames(bridge))
    console.log('electronBridge prototype：', Object.getPrototypeOf(bridge))
  }
}

/**
 * 测试连接检查监听器（仅用于调试）
 */
export const testConnectionCheck = (): void => {
  console.log('=== 测试连接检查监听器 ===')

  if (isInElectron() && window.electronBridge?.onConnectionCheck) {
    console.log('设置 onConnectionCheck 监听器')
    window.electronBridge.onConnectionCheck((data) => {
      console.log('🎯 onConnectionCheck 被触发！数据：', data)
    })
    console.log('✅ onConnectionCheck 监听器设置完成')
  } else {
    console.log('❌ 无法设置 onConnectionCheck 监听器')
    console.log('isInElectron:', isInElectron())
    console.log('onConnectionCheck 存在:', !!window.electronBridge?.onConnectionCheck)
  }

  // 也测试 notifyPageVisible
  if (window.electronBridge?.notifyPageVisible) {
    console.log('调用 notifyPageVisible 通知页面可见')
    window.electronBridge.notifyPageVisible()
  }
}

/**
 * 保存 token
 */
export const saveToken = (token: string): void => {
  if (isInElectron()) {
    window.electronBridge!.postMessage({
      action: 'saveToken',
      token
    })
  }
}

/**
 * 发送打印请求
 */
export const print = async (data: any): Promise<void> => {
  if (isInElectron()) {
    console.log('发送打印请求')
    const message: ElectronMessage = {
      action: 'print',
      data
    }
    await window.electronBridge!.postMessage(message)
  } else {
    console.error('electronBridge 未定义，无法发送消息')
  }
}

/**
 * 跳转打印机管理
 */
export const toPrintList = () => {
  if (isInElectron()) {
    window.electronBridge!.postMessage({
      action: 'toPrintList'
    })
  }
}
